/**
 * Números da aba "Visão Geral" do projeto que vão além do progresso: prazo do projeto, o que
 * precisa de atenção e em que status o trabalho aberto está parado. Tudo derivado das tarefas
 * que a tela já carrega — sem consulta extra.
 */
import type { ProjetoEtapa } from '../../../types';
import { diasParaVencer, situacaoPrazo, type SituacaoPrazo, type TarefaOrdenavel, DIAS_PROXIMO } from './tarefasProjeto.ts';

const DESCARTADOS = new Set(['rejeitado', 'cancelado']);
const situacao = (t: TarefaOrdenavel): string => t.solicitacao_status || t.status || 'novo';

const diaDe = (v: string): number => new Date(`${v.slice(0, 10)}T00:00:00`).getTime();

// ─── Prazo do projeto ─────────────────────────────────────────────────────────

export type SituacaoPrazoProjeto = 'encerrado' | 'sem_prazo' | 'atrasado' | 'vence_hoje' | 'proximo' | 'no_prazo';

export interface PrazoProjeto {
  situacao: SituacaoPrazoProjeto;
  /** Dias até o prazo (negativo = passou). null sem prazo. */
  dias: number | null;
  /** Maior vencimento entre as tarefas ainda abertas. */
  ultimoVencimentoAberto: string | null;
  /** As tarefas abertas terminam depois do prazo do projeto — o cronograma já não fecha. */
  cronogramaEstoura: boolean;
}

/**
 * Concluído/cancelado não tem prazo a cumprir. "Próximo" usa a mesma janela das tarefas
 * (DIAS_PROXIMO), pra projeto e tarefa acenderem âmbar pelo mesmo critério.
 */
export const calcularPrazoProjeto = (
  prazo: string | null | undefined,
  etapa: ProjetoEtapa,
  tarefas: TarefaOrdenavel[],
  hoje: Date = new Date(),
): PrazoProjeto => {
  const abertas = tarefas.filter(t => {
    const s = situacaoPrazo(t, hoje);
    return s !== 'encerrada' && t.vencimento;
  });
  const ultimo = abertas.reduce<string | null>(
    (acc, t) => (!acc || diaDe(t.vencimento as string) > diaDe(acc) ? (t.vencimento as string).slice(0, 10) : acc),
    null,
  );

  if (etapa === 'concluido' || etapa === 'cancelado') {
    return { situacao: 'encerrado', dias: null, ultimoVencimentoAberto: ultimo, cronogramaEstoura: false };
  }
  if (!prazo) {
    return { situacao: 'sem_prazo', dias: null, ultimoVencimentoAberto: ultimo, cronogramaEstoura: false };
  }

  const dias = diasParaVencer({ id: 0, vencimento: prazo }, hoje) as number;
  const situacaoPrazoProj: SituacaoPrazoProjeto =
    dias < 0 ? 'atrasado' : dias === 0 ? 'vence_hoje' : dias <= DIAS_PROXIMO ? 'proximo' : 'no_prazo';

  return {
    situacao: situacaoPrazoProj,
    dias,
    ultimoVencimentoAberto: ultimo,
    cronogramaEstoura: ultimo != null && diaDe(ultimo) > diaDe(prazo),
  };
};

// ─── Precisa de atenção ───────────────────────────────────────────────────────

export interface ItemAtencao<T> {
  tarefa: T;
  situacao: Extract<SituacaoPrazo, 'atrasada' | 'vence_hoje' | 'proxima'>;
  dias: number;
}

/** Atrasadas primeiro (a mais antiga no topo), depois hoje e próximas por vencimento. */
export const tarefasEmAtencao = <T extends TarefaOrdenavel>(tarefas: T[], hoje: Date = new Date()): ItemAtencao<T>[] =>
  tarefas
    .map(tarefa => ({ tarefa, situacao: situacaoPrazo(tarefa, hoje), dias: diasParaVencer(tarefa, hoje) }))
    .filter((i): i is ItemAtencao<T> =>
      i.situacao === 'atrasada' || i.situacao === 'vence_hoje' || i.situacao === 'proxima')
    .sort((a, b) => a.dias - b.dias || Number(a.tarefa.id) - Number(b.tarefa.id));

// ─── Distribuição por status ──────────────────────────────────────────────────

/** Ordem do fluxo da Solicitação; é a ordem dos segmentos na barra. */
export const ORDEM_STATUS = ['novo', 'em_analise', 'em_desenvolvimento', 'em_teste', 'concluido'] as const;

export interface Distribuicao {
  /** Contagem por status, só das válidas (sem rejeitada/cancelada), na ordem de ORDEM_STATUS
   *  e com status fora do fluxo (ex.: 'a_iniciar' de tarefa comum) no fim. */
  segmentos: { status: string; quantidade: number }[];
  total: number;
  descartadas: number;
}

export const distribuicaoPorStatus = (tarefas: TarefaOrdenavel[]): Distribuicao => {
  const cont = new Map<string, number>();
  let descartadas = 0;
  for (const t of tarefas) {
    const s = situacao(t);
    if (DESCARTADOS.has(s)) { descartadas++; continue; }
    cont.set(s, (cont.get(s) || 0) + 1);
  }
  const conhecidos: string[] = [...ORDEM_STATUS];
  const segmentos = [
    ...conhecidos.filter(s => cont.has(s)),
    ...[...cont.keys()].filter(s => !conhecidos.includes(s)).sort(),
  ].map(status => ({ status, quantidade: cont.get(status) as number }));
  return { segmentos, total: segmentos.reduce((s, x) => s + x.quantidade, 0), descartadas };
};

// ─── Próximas entregas ────────────────────────────────────────────────────────

/**
 * Tarefas em aberto que vencem de hoje em diante, pela data. Atrasadas ficam de fora — já têm o
 * bloco "Precisa de atenção"; aqui a pergunta é "o que vem a seguir".
 */
export const proximasEntregas = <T extends TarefaOrdenavel>(tarefas: T[], hoje: Date = new Date(), limite = 5): T[] =>
  tarefas
    .filter(t => {
      const s = situacaoPrazo(t, hoje);
      return s === 'vence_hoje' || s === 'proxima' || s === 'no_prazo';
    })
    .sort((a, b) =>
      diaDe(a.vencimento as string) - diaDe(b.vencimento as string) || Number(a.id) - Number(b.id))
    .slice(0, limite);

// ─── Ritmo e projeção ─────────────────────────────────────────────────────────

export interface TarefaRitmo extends TarefaOrdenavel {
  data_conclusao?: string | null;
  created_at?: string | null;
}

export interface Ritmo {
  /** Concluídas por semana (segunda a domingo), da mais antiga para a atual. Só semanas desde o
   *  início do projeto, no máximo SEMANAS_HISTORICO. */
  semanas: { inicio: string; concluidas: number }[];
  /** Média de concluídas por semana na janela recente. */
  porSemana: number;
  restantes: number;
  /** Data (yyyy-mm-dd) em que o trabalho aberto acaba mantido o ritmo; null quando não dá pra
   *  projetar com honestidade. */
  previsao: string | null;
  motivo: 'ok' | 'concluido' | 'sem_ritmo' | 'poucos_dados';
}

/** Janela do ritmo: as últimas 2 semanas dizem mais sobre o fim do projeto que o histórico todo. */
export const DIAS_JANELA_RITMO = 14;
/** Abaixo disso a projeção oscila demais para ser mostrada como data. */
export const MIN_CONCLUIDAS_PROJECAO = 3;
export const MIN_DIAS_PROJECAO = 7;
export const SEMANAS_HISTORICO = 6;

const MS_DIA = 86_400_000;

/** Meia-noite local. Timestamp completo usa o fuso local (concluída às 22h de Brasília é daquele
 *  dia, não do seguinte em UTC); data pura é tratada como dia local. */
const diaLocal = (v: string): number => {
  const d = v.length <= 10 ? new Date(`${v}T00:00:00`) : new Date(v);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const somarDias = (ms: number, dias: number): number => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dias).getTime();
};

const iso = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const segundaDaSemana = (ms: number): number => somarDias(ms, -((new Date(ms).getDay() + 6) % 7));

/**
 * Início = o mais antigo entre a criação do projeto e a das tarefas: projeto criado depois para
 * agrupar trabalho que já andava não pode parecer ter "começado ontem" e inflar o ritmo.
 */
export const calcularRitmo = (
  tarefas: TarefaRitmo[],
  criadoEm: string,
  hoje: Date = new Date(),
): Ritmo => {
  const validas = tarefas.filter(t => !DESCARTADOS.has(situacao(t)));
  const concluidas = validas.filter(t => situacao(t) === 'concluido');
  const restantes = validas.length - concluidas.length;
  const hojeMs = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();

  const inicio = Math.min(
    diaLocal(criadoEm),
    ...validas.filter(t => t.created_at).map(t => diaLocal(t.created_at as string)),
  );
  const diasDeHistorico = Math.round((hojeMs - inicio) / MS_DIA) + 1;

  const datas = concluidas.filter(t => t.data_conclusao).map(t => diaLocal(t.data_conclusao as string));

  const semanaAtual = segundaDaSemana(hojeMs);
  const semanas: Ritmo['semanas'] = [];
  for (let s = Math.max(segundaDaSemana(inicio), somarDias(semanaAtual, -(SEMANAS_HISTORICO - 1) * 7));
    s <= semanaAtual; s = somarDias(s, 7)) {
    const fim = somarDias(s, 7);
    semanas.push({ inicio: iso(s), concluidas: datas.filter(d => d >= s && d < fim).length });
  }

  // Janela nunca menor que uma semana: 2 entregas no primeiro dia não viram "14 por semana".
  const janela = Math.max(MIN_DIAS_PROJECAO, Math.min(DIAS_JANELA_RITMO, diasDeHistorico));
  const desde = somarDias(hojeMs, -(janela - 1));
  const naJanela = datas.filter(d => d >= desde && d <= hojeMs).length;
  const porSemana = (naJanela / janela) * 7;

  const base = { semanas, porSemana, restantes };
  if (restantes === 0) return { ...base, previsao: null, motivo: 'concluido' };
  if (concluidas.length < MIN_CONCLUIDAS_PROJECAO || diasDeHistorico < MIN_DIAS_PROJECAO) {
    return { ...base, previsao: null, motivo: 'poucos_dados' };
  }
  if (naJanela === 0) return { ...base, previsao: null, motivo: 'sem_ritmo' };

  const diasParaAcabar = Math.ceil(restantes / (naJanela / janela));
  return { ...base, previsao: iso(somarDias(hojeMs, diasParaAcabar)), motivo: 'ok' };
};
